<style scoped>
  canvas.crop {
    margin: auto;
    display: block;
    max-width: 100%;
  }
</style>

<template>
  <canvas
    ref="main"
    class="crop"
    @mousedown.prevent="handleDown"
    @mouseup.prevent="handleUp"
    @mousemove.prevent="handleMove"
    @mouseleave.prevent="handleUp"
    @touchstart.prevent="handleDown"
    @touchmove.prevent="handleMove"
    @touchstop="handleUp"
    @mousewheel.prevent="handleScroll"
    @DOMMouseScroll.prevent="handleScroll"
  />
</template>

<script>
import nudged from "nudged";
// import fixOrientation from "exif-normalizer";

function clamp(x, a, b) {
  return Math.max(a, Math.min(x, b));
}

export default {
  name: "ImageCrop",
  props: {
    image: {type: String, default: null},
    value: {type: String, default: null},
    maxWidth: {type: Number, default: 512},
    ratio: {type: Number, default: 1},
  },
  data() {
    return {
      img: null,
      scale: 1,
      maxScale: 1,
      x: 0,
      y: 0
    };
  },
  watch: {
    image: {
      async handler() {
        if (this.image) {
          await this.fetchImage();
          await this.$nextTick();
          await this.clampAll();
        }
      },
      deep: true,
    }
  },
  async mounted() {
    this.ctx = this.$refs.main.getContext("2d");

    await this.resize();

    if (this.image) {
      await this.fetchImage();
      this.draw();

      await this.$nextTick();
      this.clampAll(true);
    }
  },
  destroyed() {
    cancelAnimationFrame(this._animation);
  },
  methods: {
    async resize() {
      this.$refs.main.style.width = "100%";
      await this.$nextTick();

      this.box = this.$refs.main.getBoundingClientRect();

      const ratio = this.ratio;
      const width = Math.min(512, this.box.width);
      const cwidth = Math.min(this.maxWidth, this.box.width * window.devicePixelRatio);

      this.$refs.main.width = cwidth;
      this.$refs.main.height = cwidth / ratio;
      this.$refs.main.style.width = width + "px";
      this.$refs.main.style.height = (width / ratio) + "px";
      await this.$nextTick();
    },
    draw() {
      if (this.img) {
        this.ctx.clearRect(0, 0, this.cw, this.ch);
        this.ctx.save();
        this.ctx.translate(this.cw / 2, this.ch / 2);
        this.ctx.translate(this.x, this.y);
        this.ctx.scale(this.scale, this.scale);
        this.ctx.translate(
          -(this.iw / 2),
          -(this.ih / 2)
        );
        this.ctx.drawImage(this.img, 0,  0);
        this.ctx.restore();
      }
      this._animation = requestAnimationFrame(this.draw)
    },
    mouseToPoint(p) {
      let retval = [
        ((p.pageX - this.box.left)),  // / this.box.width - 0.5) * (this.cw * this.scale) + this.x,
        ((p.pageY - this.box.top)),  // / this.box.height - 0.5) * (this.ch * this.scale) + this.y
      ];
      return retval;
    },
    getTouches(e) {
      let touches = [];
      if (this.box) {
        if (e.touches) {
          Array.prototype.forEach.call(e.touches, (touch) => {
            touches.push(this.mouseToPoint(touch));
          });
        } else {
          touches.push(this.mouseToPoint(e));
        }
      }
      return touches;
    },
    getCenter(touches) {
      let retval = [0, 0];
      for (let t of touches) {
        retval[0] += t[0];
        retval[1] += t[1];
      }
      return retval.map(a => a / touches.length);
    },
    clampAll(first=false) {
      this.scale = clamp(this.scale, this.maxScale, 1);

      let zw = (this.iw * this.scale - this.cw) / 2;
      let zh = (this.ih * this.scale - this.ch) / 2;
      this.x = clamp(this.x, -zw, zw);
      this.y = clamp(this.y, -zh, zh);

      // debounce output
      clearTimeout(this._clampAll);
      if (! first) {
        this._clampAll = setTimeout(() => {
          this.$emit("input", this.ctx.canvas.toDataURL("image/jpg"));
        }, 200);
      }
    },
    handleScroll(e) {
      let delta = Math.max(-1, Math.min(1, (e.wheelDelta || -e.detail))) / 10;
      let nscale = clamp(this.scale * (1.0 + delta), this.maxScale, 1);
      delta = (nscale - this.scale) * 2;

      // let zoom = this.mouseToPoint(e);

      this.x += this.x * delta;
      this.y += this.y * delta;
      this.scale = nscale;
      this.clampAll();
    },
    handleUp() {
      this.down = false;
      this.touches = [];
    },
    handleDown(e) {
      this.down = true;
      this.touches = this.getTouches(e);
    },
    handleMove(e) {
      if (e.type == "mousemove") {
        if (!this.down) {
          return;
        }
      }

      let range = this.getTouches(e);
      // let center = this.getCenter(range);
      if (this.touches && this.touches.length > 0 &&
          this.touches.length == range.length) {
        let trans = nudged.estimate('TS', this.touches, range);
        // let loc = trans.getTranslation();
        let scaled = trans.getScale();
        let pos = trans.transform([this.x, this.y]);
        this.scale *= scaled;

        // this.x += loc[0] * this.scale;// + this.iw * (1 - scaled) * 0.25;
        // this.y += loc[1] * this.scale;// + this.ih * (1 - scaled) * 0.25;
        this.x = pos[0];
        this.y = pos[1];

        this.clampAll();
      }

      this.touches = range;
    },
    async fetchImage() {
      if (this.image) {
        this.img = new Image();
        this.img.crossOrigin = "anonymous";
        this.img.src = this.image;
        // TODO: this.img.src = await fixOrientation(this.image);
        await new Promise((resolve, reject) =>  {
          this.img.onerror = reject;
          this.img.onload = () => {
            this.iw = this.img.width;
            this.ih = this.img.height;
            this.cw = this.$refs.main.width;
            this.ch = this.$refs.main.height;
            this.x = 0;
            this.y = 0;

            this.scale = Math.max(this.ch / this.ih, this.cw / this.iw);
            this.maxScale = this.scale;

            resolve();
          };
        });
      } else {
        this.img = null;
      }
    }
  }
}
</script>
